<script lang="ts" setup>
  import { getProductShopProductsProductIdGet } from '@/shared/api/intranet'
  import { useNotifications } from '@/shared/lib'
  import { ImagePlaceholder } from '@/shared/ui/svg'

  import type { AttributeGroupOut, ProductOutFull, ProductValueOut, SelectedAttributeValue } from '@/entities/shop'

  import {
    resolveShopItemCurrentStock,
    SHOP_ITEM_PAGE_KEY,
    ShopItemGallery,
    ShopItemInfo,
    ShopItemPageHeader,
    ShopItemPageSkeleton,
  } from '@/views/shop/item'

  definePage({
    meta: { layout: 'baseNew' },
  })

  const route = useRoute('/shop/item/[id]/')
  const router = useRouter()
  const productId = computed(() => Number(route.params.id))

  const { errorNotification } = useNotifications()

  const isLoading = ref(false)
  const product = ref<Nullable<ProductOutFull>>(null)

  const mainAttribute = ref<Nullable<SelectedAttributeValue>>(null)
  const secondAttribute = ref<Nullable<SelectedAttributeValue>>(null)

  const selectedAttributes = computed((): SelectedAttributeValue[] => {
    return [mainAttribute.value, secondAttribute.value].filter((item): item is SelectedAttributeValue => !!item)
  })

  const isSingleAttribute = computed(() => product.value?.attributes.length === 1)

  const availableInStock = (value1Id: number, value2Id?: number) => {
    const item = product.value?.stock.find((stockItem) => {
      if (value2Id) {
        return (
          (stockItem.value_1_id === value1Id && stockItem.value_2_id === value2Id) ||
          (stockItem.value_1_id === value2Id && stockItem.value_2_id === value1Id)
        )
      }
      return stockItem.value_1_id === value1Id
    })

    return !!item?.quantity
  }

  const selectAttributeOption = (
    value: ProductValueOut,
    attributeName: string,
    attributeId: number,
    main?: boolean,
  ) => {
    if (!product.value) {
      return
    }

    const selected = { ...value, attributeName, attributeId }

    if (!main && !isSingleAttribute.value) {
      secondAttribute.value = selected
      return
    }

    mainAttribute.value = selected

    if (isSingleAttribute.value) {
      return
    }

    const attribute2 = product.value.attributes[1]

    if (!attribute2) {
      return
    }

    const matchedValue = attribute2.values.find((value2) => availableInStock(mainAttribute.value!.id, value2?.id))

    secondAttribute.value = matchedValue
      ? { ...matchedValue, attributeName: attribute2.name, attributeId: attribute2.id }
      : null
  }

  const findFirstAvailablePair = (attribute1: AttributeGroupOut, attribute2: AttributeGroupOut) => {
    for (const value1 of attribute1.values) {
      for (const value2 of attribute2.values) {
        if (availableInStock(value1.id, value2.id)) {
          return { value1, value2 }
        }
      }
    }

    return null
  }

  const autoSelectSingleAttribute = (attribute: AttributeGroupOut) => {
    const availableValue = attribute.values.find((item) => availableInStock(item.id))

    if (!availableValue) {
      return false
    }

    selectAttributeOption(availableValue, attribute.name, attribute.id)
    return true
  }

  const autoSelectFallbackMain = (attribute: AttributeGroupOut) => {
    const fallbackValue = attribute.values[0]

    if (!mainAttribute.value && fallbackValue) {
      selectAttributeOption(fallbackValue, attribute.name, attribute.id, true)
    }
  }

  const autoSelectOnLoad = () => {
    if (!product.value) {
      return
    }

    const attribute1 = product.value.attributes[0]
    const attribute2 = product.value.attributes[1]

    if (!attribute1) {
      return
    }

    if (attribute2) {
      const availablePair = findFirstAvailablePair(attribute1, attribute2)

      if (availablePair) {
        selectAttributeOption(availablePair.value1, attribute1.name, attribute1.id, true)
        selectAttributeOption(availablePair.value2, attribute2.name, attribute2.id)
        return
      }

      autoSelectFallbackMain(attribute1)
      return
    }

    if (autoSelectSingleAttribute(attribute1)) {
      return
    }

    autoSelectFallbackMain(attribute1)
  }

  const currentStock = computed(() =>
    resolveShopItemCurrentStock(product.value, mainAttribute.value, secondAttribute.value),
  )

  const canBeOrdered = computed(() => Boolean(product.value?.can_be_ordered))

  const isOutOfStock = computed(() => product.value?.stock.every((item) => item.quantity === 0) ?? false)

  const showAttributesList = computed(() => {
    if (!product.value || product.value.attributes.length === 0) {
      return false
    }
    return !isSingleAttribute.value || !!product.value.attributes[0]?.name
  })

  const resetAttributes = () => {
    mainAttribute.value = null
    secondAttribute.value = null
  }

  const getFullProduct = async () => {
    isLoading.value = true
    resetAttributes()

    const { data, error, response } = await getProductShopProductsProductIdGet({
      path: {
        product_id: productId.value,
      },
    })

    if (error && response?.status === 404) {
      isLoading.value = false
      errorNotification('shop_item_out_of_stock_error')
      router.push('/shop/')
      return
    }

    product.value = data!
    isLoading.value = false
    autoSelectOnLoad()
  }

  const handleEdit = (id: number) => {
    router.push({ name: '/shop/item/[id]/edit/', params: { id }, state: { from: route.fullPath } })
  }

  const handleGoToCart = () => {
    router.push({ name: '/shop/cart/', state: { from: route.fullPath } })
  }

  const handleNotFound = () => {
    router.push({ name: '/shop/', state: { from: route.fullPath } })
  }

  provide(SHOP_ITEM_PAGE_KEY, {
    selectAttributeOption,
    selectedAttributes,
    currentStock,
    canBeOrdered,
    isOutOfStock,
    showAttributesList,
    handleGoToCart,
    handleNotFound,
    handleRefresh: getFullProduct,
    handleEdit,
  })

  onMounted(() => {
    getFullProduct()
  })

  watch(productId, getFullProduct)
</script>

<template>
  <div class="wide-responsive flex w-full flex-col p-6">
    <ShopItemPageSkeleton v-if="isLoading" />

    <template v-else-if="product">
      <ShopItemPageHeader :product-name="product.name" />

      <div class="mt-4 flex flex-nowrap! items-start justify-start gap-4 px-0 pl-1.5">
        <ShopItemGallery
          v-if="product.attachments?.length"
          :images="product.attachments"
        />
        <ImagePlaceholder
          v-else
          class="rounded-10 text-subtitle aspect-square max-h-87.5 w-full max-w-84.5 shrink-0 bg-white max-sm:max-w-full"
        />

        <ShopItemInfo
          :product="product"
          :is-loading="isLoading"
        />
      </div>
    </template>
  </div>
</template>
